<template>
  <div class="min-h-screen flex flex-col bg-white text-fg font-sans">
    <!-- Header -->
    <header
      data-testid="header"
      class="fixed top-0 left-0 right-0 z-40 pt-safe backdrop-blur-xl backdrop-saturate-150 border-b transition-all duration-300 bg-white/85 border-line/60 shadow-sm"
    >
      <div class="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <router-link
          data-testid="link-logo"
          class="flex items-center gap-2 cursor-pointer"
          to="/"
        >
          <img src="/images/tble_logo2.svg" alt="티블비즈" class="h-7 w-auto" />
        </router-link>

        <nav class="hidden md:flex items-center gap-6">
          <button
            data-testid="nav-서비스"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('services')"
          >
            서비스
          </button>
          <button
            data-testid="nav-혜택"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('benefits')"
          >
            혜택
          </button>
          <button
            data-testid="nav-성과"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('performance')"
          >
            성과
          </button>
          <button
            data-testid="nav-진행 방식"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('process')"
          >
            진행 방식
          </button>
          <button
            data-testid="nav-FAQ"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('faq')"
          >
            FAQ
          </button>
        </nav>

        <div class="flex items-center gap-3">
          <button
            data-testid="button-header-cta"
            class="hidden md:inline-flex items-center px-5 py-2.5 rounded-[12px] border text-sm font-semibold backdrop-blur-sm transition-colors cursor-pointer border-line bg-white/60 text-fg hover:bg-white"
            @click="openModal"
          >
            무료 상담 신청
          </button>
          <button
            data-testid="button-mobile-menu"
            class="md:hidden -mr-2 flex items-center justify-center min-w-[44px] min-h-[44px] transition-colors cursor-pointer text-fg"
            aria-label="메뉴 열기"
            @click="isMobileMenuOpen = !isMobileMenuOpen"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-menu"
              aria-hidden="true"
            >
              <path d="M4 5h16" />
              <path d="M4 12h16" />
              <path d="M4 19h16" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Nav Drawer -->
      <div v-if="isMobileMenuOpen" class="md:hidden bg-white border-b border-line px-4 py-4 space-y-3 shadow-md">
        <button
          class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg"
          @click="navToHomeSection('services')"
        >
          서비스
        </button>
        <button
          class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg"
          @click="navToHomeSection('benefits')"
        >
          혜택
        </button>
        <button
          class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg"
          @click="navToHomeSection('performance')"
        >
          성과
        </button>
        <button
          class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg"
          @click="navToHomeSection('process')"
        >
          진행 방식
        </button>
        <button
          class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg"
          @click="navToHomeSection('faq')"
        >
          FAQ
        </button>
        <button
          data-testid="button-header-cta"
          class="w-full py-3 rounded-xl bg-brand-500 text-white font-bold text-sm"
          @click="openModal"
        >
          무료 상담 신청
        </button>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1">
      <div class="pt-16 pb-24 md:pb-0">
        <!-- Hero Section -->
        <section class="py-16 md:py-24 bg-gradient-to-br from-brand-50 to-white">
          <div class="max-w-7xl mx-auto px-4 md:px-6">
            <span class="text-xs font-bold uppercase tracking-widest text-brand-500 mb-3 block">PROCESS</span>
            <h1 class="text-2xl md:text-4xl font-bold text-fg leading-tight mb-5 whitespace-pre-line">
              상담부터 결과보고까지,<br />
티블의 5단계 운영 프로세스
            </h1>
            <p class="text-base text-fg-muted leading-relaxed max-w-xl">
              처음 시작하는 사장님도 막막하지 않도록, 모든 단계를 투명하게 안내드립니다.
            </p>
          </div>
        </section>

        <!-- 5 Steps List Section -->
        <section class="py-16 bg-white">
          <div class="max-w-7xl mx-auto px-4 md:px-6">
            <div class="space-y-6 mb-10">
              <!-- Step 01 -->
              <div data-testid="process-page-step-01" class="bg-bg-soft rounded-[16px] p-6 md:p-8 border border-line">
                <div class="flex items-start gap-5">
                  <div class="flex-shrink-0 w-14 h-14 rounded-[14px] bg-brand-500 flex flex-col items-center justify-center">
                    <span class="text-white text-[10px] font-semibold uppercase tracking-wide">STEP</span>
                    <span class="text-white text-base font-bold leading-none">01</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex flex-wrap items-center gap-3 mb-3">
                      <h3 class="text-lg font-bold text-fg">상담</h3>
                      <span class="text-sm text-brand-600 font-medium px-3 py-1 rounded-full bg-brand-50">1~3 영업일</span>
                    </div>
                    <p class="text-base text-fg-muted leading-relaxed mb-5">
                      비즈니스 현황과 목표를 함께 확인합니다. 업종·채널·경쟁 현황을 파악해 최적의 운영 방향을 제안드립니다.
                    </p>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p class="text-xs font-bold uppercase tracking-wide text-fg-subtle mb-2">사장님 준비</p>
                        <ul class="space-y-1.5">
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>현재 매출·리뷰 상황 공유
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>고민과 목표 전달
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>운영 예산 범위 안내
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p class="text-xs font-bold uppercase tracking-wide text-brand-500 mb-2">티블 담당</p>
                        <ul class="space-y-1.5">
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>채널·키워드 진단
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>업종별 성과 사례 제시
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>추천 캠페인 방향 제안
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 02 -->
              <div data-testid="process-page-step-02" class="bg-bg-soft rounded-[16px] p-6 md:p-8 border border-line">
                <div class="flex items-start gap-5">
                  <div class="flex-shrink-0 w-14 h-14 rounded-[14px] bg-brand-500 flex flex-col items-center justify-center">
                    <span class="text-white text-[10px] font-semibold uppercase tracking-wide">STEP</span>
                    <span class="text-white text-base font-bold leading-none">02</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex flex-wrap items-center gap-3 mb-3">
                      <h3 class="text-lg font-bold text-fg">맞춤 제안</h3>
                      <span class="text-sm text-brand-600 font-medium px-3 py-1 rounded-full bg-brand-50">2~5 영업일</span>
                    </div>
                    <p class="text-base text-fg-muted leading-relaxed mb-5">
                      상담 내용을 바탕으로 채널 조합, 운영 범위, 예산안을 설계합니다. 광고주가 확인하고 승인하는 단계입니다.
                    </p>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p class="text-xs font-bold uppercase tracking-wide text-fg-subtle mb-2">사장님 준비</p>
                        <ul class="space-y-1.5">
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>제안서 검토
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>운영 범위 확인
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>계약 승인
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p class="text-xs font-bold uppercase tracking-wide text-brand-500 mb-2">티블 담당</p>
                        <ul class="space-y-1.5">
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>캠페인 설계서 작성
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>채널별 예산 배분
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>운영 일정 확정
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 03 -->
              <div data-testid="process-page-step-03" class="bg-bg-soft rounded-[16px] p-6 md:p-8 border border-line">
                <div class="flex items-start gap-5">
                  <div class="flex-shrink-0 w-14 h-14 rounded-[14px] bg-brand-500 flex flex-col items-center justify-center">
                    <span class="text-white text-[10px] font-semibold uppercase tracking-wide">STEP</span>
                    <span class="text-white text-base font-bold leading-none">03</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex flex-wrap items-center gap-3 mb-3">
                      <h3 class="text-lg font-bold text-fg">체험단 모집</h3>
                      <span class="text-sm text-brand-600 font-medium px-3 py-1 rounded-full bg-brand-50">1~2주</span>
                    </div>
                    <p class="text-base text-fg-muted leading-relaxed mb-5">
                      52만 인플루언서 풀에서 업종·영향력·활동 기준으로 리뷰어를 선정합니다. 명단을 광고주에게 공유 후 진행합니다.
                    </p>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p class="text-xs font-bold uppercase tracking-wide text-fg-subtle mb-2">사장님 준비</p>
                        <ul class="space-y-1.5">
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>리뷰어 명단 확인
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>방문/제품 제공 기준 안내
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p class="text-xs font-bold uppercase tracking-wide text-brand-500 mb-2">티블 담당</p>
                        <ul class="space-y-1.5">
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>신청 검수·자격 확인
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>영향력·활동성 기준 선정
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>리뷰어 가이드 전달
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 04 -->
              <div data-testid="process-page-step-04" class="bg-bg-soft rounded-[16px] p-6 md:p-8 border border-line">
                <div class="flex items-start gap-5">
                  <div class="flex-shrink-0 w-14 h-14 rounded-[14px] bg-brand-500 flex flex-col items-center justify-center">
                    <span class="text-white text-[10px] font-semibold uppercase tracking-wide">STEP</span>
                    <span class="text-white text-base font-bold leading-none">04</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex flex-wrap items-center gap-3 mb-3">
                      <h3 class="text-lg font-bold text-fg">리뷰 업로드</h3>
                      <span class="text-sm text-brand-600 font-medium px-3 py-1 rounded-full bg-brand-50">2~4주</span>
                    </div>
                    <p class="text-base text-fg-muted leading-relaxed mb-5">
                      리뷰어가 직접 방문·체험·제품 사용 후 콘텐츠를 제작하고 발행합니다. 티블이 가이드·검수·발행·노출 관리를 모두 담당합니다.
                    </p>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p class="text-xs font-bold uppercase tracking-wide text-fg-subtle mb-2">사장님 준비</p>
                        <ul class="space-y-1.5">
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>승인
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>필요 시 방문 예약 안내
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p class="text-xs font-bold uppercase tracking-wide text-brand-500 mb-2">티블 담당</p>
                        <ul class="space-y-1.5">
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>콘텐츠 미션 가이드
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>제작 검수
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>발행 및 노출 관리
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>8가지 항목 품질 검수
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 05 -->
              <div data-testid="process-page-step-05" class="bg-bg-soft rounded-[16px] p-6 md:p-8 border border-line">
                <div class="flex items-start gap-5">
                  <div class="flex-shrink-0 w-14 h-14 rounded-[14px] bg-brand-500 flex flex-col items-center justify-center">
                    <span class="text-white text-[10px] font-semibold uppercase tracking-wide">STEP</span>
                    <span class="text-white text-base font-bold leading-none">05</span>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex flex-wrap items-center gap-3 mb-3">
                      <h3 class="text-lg font-bold text-fg">결과보고</h3>
                      <span class="text-sm text-brand-600 font-medium px-3 py-1 rounded-full bg-brand-50">캠페인 종료 후 3영업일 내</span>
                    </div>
                    <p class="text-base text-fg-muted leading-relaxed mb-5">
                      광고주 대시보드와 보고서로 누적 성과를 확인합니다. 다음 회차 캠페인 방향도 함께 제안드립니다.
                    </p>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p class="text-xs font-bold uppercase tracking-wide text-fg-subtle mb-2">사장님 준비</p>
                        <ul class="space-y-1.5">
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>대시보드 확인
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-fg-subtle flex-shrink-0"></span>다음 액션 결정
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p class="text-xs font-bold uppercase tracking-wide text-brand-500 mb-2">티블 담당</p>
                        <ul class="space-y-1.5">
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>누적 성과 보고서 제공
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>키워드 유입·조회수 분석
                          </li>
                          <li class="text-sm text-fg-muted flex items-start gap-2">
                            <span class="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-400 flex-shrink-0"></span>다음 회차 제안
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="text-center">
              <button
                data-testid="button-process-page-cta"
                class="px-8 py-4 rounded-[12px] bg-brand-500 text-white font-semibold text-lg hover:bg-brand-600 transition-colors cursor-pointer"
                @click="openModal"
              >
                지금 무료 상담 신청하기
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-fg text-white/70 pt-12 pb-28 md:pb-12">
      <div class="max-w-7xl mx-auto px-4 md:px-6">
        <div class="mb-8 flex items-center gap-3">
          <img src="/images/tble_logo2.svg" alt="티블비즈" class="h-7 w-auto" />
        </div>
        <p class="text-sm text-white/50 mb-8 -mt-4">다채널 리뷰 마케팅으로 매출을 올리는 광고주 서비스</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs leading-relaxed mb-8 text-white/50">
          <div class="space-y-1">
            <p>상호명: 주식회사 쓰리얼라이언스</p>
            <p>대표이사: 성동효</p>
            <p>주소: 서울시 강남구 학동로 44길 16, 1F</p>
            <p>사업자등록번호: 217-81-47577</p>
            <p>통신판매등록번호: 제2025-서울강남-04400호</p>
          </div>
          <div class="space-y-1">
            <p>개인정보 보호 최고책임자: 성동효</p>
            <p>이메일: help@3alliance.co.kr</p>
            <!-- <p>대표전화: 1833-5985</p> -->
            <!-- <p>FAX: 0504-334-6358</p> -->
            <p>상담시간: 10:00 ~ 18:00 · 토·일·공휴일 휴무</p>
          </div>
        </div>

        <div class="border-t border-white/10 pt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-1 md:gap-4">
          <p class="text-xs text-white/40 py-2">© 2026 주식회사 쓰리얼라이언스. All rights reserved.</p>
          <div class="flex gap-4 -my-1">
            <a href="#" data-testid="footer-link-개인정보 처리방침" class="inline-flex items-center min-h-[44px] text-xs text-white/50 hover:text-white transition-colors" @click.prevent>개인정보 처리방침</a>
            <a href="#" data-testid="footer-link-이용약관" class="inline-flex items-center min-h-[44px] text-xs text-white/50 hover:text-white transition-colors" @click.prevent>이용약관</a>
          </div>
        </div>
      </div>
    </footer>

    <!-- Floating Contact Button -->
    <button
      type="button"
      data-testid="button-floating-contact"
      aria-label="무료 상담 신청"
      class="fixed bottom-6 right-4 md:bottom-8 md:right-6 z-[9998] flex items-center gap-2 pl-4 pr-5 h-14 rounded-full bg-brand-500 text-white font-semibold text-sm shadow-[0_8px_24px_rgba(242,178,42,0.45)] hover:bg-brand-600 hover:shadow-[0_12px_28px_rgba(242,178,42,0.55)] transition-all hover:scale-105 cursor-pointer"
      @click="toggleModal"
    >
      <span class="flex items-center justify-center w-7 h-7 rounded-full bg-white/15">
        <img src="/images/headset.svg" alt="" class="w-4 h-4 brightness-0 invert" />
      </span>
      <span>문의하기</span>
    </button>

    <!-- Consultation Widget Modal -->
    <ConsultationModal :is-open="isModalOpen" @close="isModalOpen = false" />
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import ConsultationModal from '../common/ConsultationModal.vue';

const router = useRouter();
const isMobileMenuOpen = ref(false);
const isModalOpen = ref(false);

const navToHomeSection = (sectionId) => {
  isMobileMenuOpen.value = false;
  router.push({ path: '/', hash: '#' + sectionId });
};

const openModal = () => {
  isModalOpen.value = true;
};

const toggleModal = () => {
  isModalOpen.value = !isModalOpen.value;
};
</script>
